import { ArrowLeft01Icon, Compass01Icon, SquareLock02Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import { useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { usePapeis } from '@/lib/sessao'
import { useTituloDaPagina } from '@/lib/titulo'
import { inicioDoPerfil, perfilDaPessoa } from '@/lib/visoes'

import { LinkInterno } from '@/components/heimdall/link-interno'
import { TelaSimples } from '@/components/layout/tela-simples'
import { Button } from '@/components/ui/button'

type Icone = typeof Compass01Icon

/** Botão claro para usar dentro do bloco vinho. */
const botaoNoVinho =
  'h-10 rounded-full border-primary-foreground/30 bg-transparent px-4 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground dark:border-primary-foreground/30 dark:bg-transparent dark:hover:bg-primary-foreground/10'

/** Aviso em tela cheia (fora do menu) com o caminho de volta para a visão da pessoa. */
function TelaAviso({
  icone,
  titulo,
  descricao,
  endereco,
}: {
  icone: Icone
  titulo: string
  descricao: ReactNode
  /** Endereço que não abriu (mostrado para a pessoa conferir). */
  endereco: string
}) {
  const router = useRouter()
  const papeis = usePapeis()
  const inicio = inicioDoPerfil(perfilDaPessoa(papeis), papeis)

  return (
    <TelaSimples>
      <section className="heimdall-block-primary flex flex-col gap-5 p-6 md:p-8">
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className="hidden size-12 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 sm:flex"
          >
            <HugeiconsIcon icon={icone} strokeWidth={2} className="size-6" />
          </span>
          <div className="flex max-w-2xl min-w-0 flex-col gap-2">
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">{titulo}</h1>
            <div className="text-base text-primary-foreground/85">{descricao}</div>
            <p className="mt-1 w-fit max-w-full truncate rounded-full bg-primary-foreground/10 px-3 py-1 font-mono text-xs text-primary-foreground/80">
              {endereco}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:pl-16">
          <Button
            className="h-10 rounded-full bg-card px-4 text-card-foreground hover:bg-card/90"
            nativeButton={false}
            render={<LinkInterno para={inicio} />}
          >
            Ir para o início
          </Button>
          <Button variant="outline" className={botaoNoVinho} onClick={() => router.history.back()}>
            <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} data-icon="inline-start" />
            Voltar
          </Button>
        </div>
      </section>
    </TelaSimples>
  )
}

/** Endereço que não existe no painel. */
export function PaginaNaoEncontrada({ endereco }: { endereco: string }) {
  useTituloDaPagina('Página não encontrada')
  return (
    <TelaAviso
      icone={Compass01Icon}
      titulo="Página não encontrada"
      descricao="Este endereço não existe no Heimdall. Ele pode ter mudado com a renovação do painel, ou ter sido digitado errado."
      endereco={endereco}
    />
  )
}

/** Página que existe, mas os papéis da pessoa não abrem. */
export function SemPermissao({
  endereco,
  visaoPedida,
}: {
  endereco: string
  /** Nome da visão que a página exige, se for o caso (ex.: "Gestão"). */
  visaoPedida?: string
}) {
  useTituloDaPagina('Sem permissão')
  return (
    <TelaAviso
      icone={SquareLock02Icon}
      titulo="Esta tela não está liberada para você"
      descricao={
        <>
          {visaoPedida ? (
            <>
              Ela faz parte da visão <span className="font-medium">{visaoPedida}</span>.{' '}
            </>
          ) : null}
          Se você precisa dela no seu trabalho, peça acesso a um administrador do Heimdall.
        </>
      }
      endereco={endereco}
    />
  )
}
